import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchReadingList, fetchPublicCategories, fetchPublicDifficulties } from "./readingService";
import "./ReadingListPage.css";

const difficultyColor = (name) => {
  const map = { beginner: "#4caf50", intermediate: "#ff9800", advanced: "#e91e63" };
  return map[name?.toLowerCase()] || "#999";
};

const ReadingListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [difficultyId, setDifficultyId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPublicCategories().then(setCategories);
    fetchPublicDifficulties().then(setDifficulties);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await fetchReadingList({
      page, size: 20,
      search: search || undefined,
      difficultyId: difficultyId || undefined,
      categoryId: categoryId || undefined,
    });
    if (res.status === 200 && res.data?.status) {
      setItems(res.data.data || []);
      setTotalPages(res.data.totalPages || 0);
      setTotalItems(res.data.totalItems || 0);
    } else if (res.status === 403) {
      setError("Access denied. You may need an active subscription to view reading passages.");
      setItems([]);
    } else {
      setError(res.message || "Failed to load passages.");
      setItems([]);
      setTotalPages(0);
      setTotalItems(0);
    }
    setLoading(false);
  }, [page, search, difficultyId, categoryId]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (setter) => (e) => {
    setPage(0);
    setter(e.target.value);
  };

  return (
    <>
      <Navbar />
      <div className="reading-page">
        <h1 className="reading-title">📰 Reading</h1>
        <p className="reading-subtitle">Improve your reading comprehension with curated passages.</p>

        <div className="reading-filters">
          <input
            className="reading-search" type="text" placeholder="Search passages..."
            value={search} onChange={handleFilterChange(setSearch)}
          />
          <select className="reading-filter-select" value={difficultyId} onChange={handleFilterChange(setDifficultyId)}>
            <option value="">All Levels</option>
            {difficulties.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select className="reading-filter-select" value={categoryId} onChange={handleFilterChange(setCategoryId)}>
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {totalItems > 0 && <p className="reading-count">{totalItems} passages found</p>}

        {error ? (
          <p className="reading-empty" style={{ color: "#e53935" }}>{error}</p>
        ) : loading ? (
          <p className="reading-loading">Loading...</p>
        ) : items.length === 0 ? (
          <p className="reading-empty">No passages found.</p>
        ) : (
          <div className="reading-grid">
            {items.map((p) => (
              <div key={p.id} className="reading-card" onClick={() => navigate(`/study/reading/${p.id}`)}>
                <div className="reading-card-header">
                  <h3 className="reading-card-title">{p.title}</h3>
                  <span className="reading-badge" style={{ background: difficultyColor(p.difficulty) }}>
                    {p.difficulty}
                  </span>
                </div>
                <div className="reading-card-meta">
                  {p.category && <span className="reading-tag">{p.category}</span>}
                  {p.wordCount && <span className="reading-tag reading-tag-words">📖 {p.wordCount} words</span>}
                </div>
                {p.excerpt && <p className="reading-card-excerpt">{p.excerpt}</p>}
                <div className="reading-card-cta">Read Now →</div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="reading-pagination">
            <button disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
            <span>Page {page + 1} / {totalPages}</span>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default ReadingListPage;
